import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  useDeleteCaregiverMutation,
  useDeleteEmergencyContactMutation,
  usePatientCareTeamCaregiverQuery,
  usePatientCareTeamQuery,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';

const getPCPName = (
  name: string,
  type: 'Unknown' | 'External' | 'Internal',
) => {
  switch (type) {
    case 'Unknown':
      return 'Unknown';
    case 'External':
      return '-';
    case 'Internal':
      return name;
  }
};

export const useCareTeam = (patient_id: string) => {
  const { data: careTeamData } = usePatientCareTeamQuery(patient_id);
  const { data: caregiver } = usePatientCareTeamCaregiverQuery(patient_id);
  const deleteCaregiverMutation = useDeleteCaregiverMutation(patient_id);
  const deleteEmergencyMutation = useDeleteEmergencyContactMutation(patient_id);

  const [showPracticeDetails, setShowPracticeDetails] = useState(false);

  const togglePracticeDetails = () => {
    setShowPracticeDetails(prevState => !prevState);
  };

  const handleDeleteCaregiver = async () => {
    try {
      await deleteCaregiverMutation.mutateAsync();
      toast.success(`Caregiver successfully deleted.`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteEmergencyContact = async (pec_id: string) => {
    try {
      await deleteEmergencyMutation.mutateAsync(pec_id);
      toast.success(`Contact successfully deleted.`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    practice: {
      partnerLogo: careTeamData?.practice_logo?.thumbnail_xsmall || '',
      practiceName: careTeamData?.practice.name,
      partnerName: careTeamData?.partner_name,
      email: careTeamData?.practice.email || '',
      phone: careTeamData?.practice.phone_number || '',
      fax: careTeamData?.practice.fax_number || '',
      location: careTeamData?.practice.address?.full_address_line || '',
      inactive: careTeamData?.practice.inactive,
    },
    pcp: {
      name: careTeamData?.primary_care_provider.name || '',
      email: careTeamData?.primary_care_provider.email || '',
      phone: careTeamData?.primary_care_provider.phone_number || '',
      slack_link: careTeamData?.primary_care_provider.slack_link || '',
      inactive: !!careTeamData?.primary_care_provider.inactive,
      id: careTeamData?.primary_care_provider.id || '',
      region: careTeamData?.primary_care_provider.region || '',
    },
    neurology: {
      name: careTeamData?.neurology_provider.name || '',
      email: careTeamData?.neurology_provider.email || '',
      phone: careTeamData?.neurology_provider.phone_number || '',
      slack_link: careTeamData?.neurology_provider.slack_link || '',
      inactive: !!careTeamData?.neurology_provider.inactive,
      id: careTeamData?.neurology_provider.id || '',
    },
    homeHealthProvider: {
      name: careTeamData?.home_health_provider.name || '',
      email: careTeamData?.home_health_provider.email || '',
      phone: careTeamData?.home_health_provider.phone_number || '',
      slack_link: careTeamData?.home_health_provider.slack_link || '',
      inactive: !!careTeamData?.home_health_provider.inactive,
      id: careTeamData?.home_health_provider.id || '',
    },
    socialWorkerProvider: {
      name: careTeamData?.social_worker_provider?.name || '',
      email: careTeamData?.social_worker_provider?.email || '',
      phone: careTeamData?.social_worker_provider?.phone_number || '',
      slack_link: careTeamData?.social_worker_provider?.slack_link || '',
      inactive: !!careTeamData?.social_worker_provider?.inactive,
      id: careTeamData?.social_worker_provider?.id || '',
    },
    careNavigator: {
      name: careTeamData?.care_navigator.name || '',
      email: careTeamData?.care_navigator.email || '',
      phone: careTeamData?.care_navigator.phone_number || '',
    },
    assessingProvider: {
      name: careTeamData?.assessing_provider.name || '',
      email: careTeamData?.assessing_provider.email || '',
      phone: careTeamData?.assessing_provider.phone_number || '',
      slack_link: careTeamData?.assessing_provider.slack_link || '',
      inactive: careTeamData?.assessing_provider.inactive,
    },
    geropsychProvider: {
      name: careTeamData?.geropsych_provider.name || '',
      email: careTeamData?.geropsych_provider.email || '',
      phone: careTeamData?.geropsych_provider.phone_number || '',
      slack_link: careTeamData?.geropsych_provider.slack_link || '',
      inactive: careTeamData?.geropsych_provider.inactive,
    },
    cardiologyProvider: {
      name: careTeamData?.cardiology_provider.name || '',
      email: careTeamData?.cardiology_provider.email || '',
      phone: careTeamData?.cardiology_provider.phone_number || '',
      slack_link: careTeamData?.cardiology_provider.slack_link || '',
      inactive: careTeamData?.cardiology_provider.inactive,
    },
    palliativeCare: {
      name: careTeamData?.palliative_care_provider.name || '',
      email: careTeamData?.palliative_care_provider.email || '',
      phone: careTeamData?.palliative_care_provider.phone_number || '',
      slack_link: careTeamData?.palliative_care_provider.slack_link || '',
      inactive: careTeamData?.palliative_care_provider.inactive,
    },
    careManager: {
      name: careTeamData?.care_manager.name || '',
      email: careTeamData?.care_manager.email || '',
      phone_number: careTeamData?.care_manager.phone_number || '',
      // slack_link: careTeamData?.care_manager.slack_link || '',
    },
    showPracticeDetails,
    togglePracticeDetails,
    primaryCaregiver: caregiver,
    handleDeleteCaregiver,
    handleDeleteEmergencyContact,
    emergencyContacts: careTeamData?.emergency_contacts || [],
    provider_type: careTeamData?.provider_type || 'Unknown',
    internalPCPName: getPCPName(
      careTeamData?.primary_care_provider.name || '',
      careTeamData?.provider_type || 'Unknown',
    ),
  };
};
